import { useNavigate } from 'react-router-dom';
import { ChevronsUpDown, KeyRound, Laptop, LogOut, Moon, Sun, User as UserIcon } from 'lucide-react';
import { Dropdown, DropdownItem } from '@/components/ui/Dropdown';
import { Avatar } from '@/components/ui/Avatar';
import { useAuth } from '@/hooks/useAuth';
import { useTheme } from '@/hooks/useTheme';

interface ProfileMenuProps {
  /** 'avatar' (default) — small round trigger for the topbar. 'card' — the sidebar's pinned account card (name/role + chevron), opens upward. */
  variant?: 'avatar' | 'card';
}

export function ProfileMenu({ variant = 'avatar' }: ProfileMenuProps) {
  const { user, logout } = useAuth();
  const { resolvedTheme, toggle } = useTheme();
  const navigate = useNavigate();

  if (!user) {
    return null;
  }

  const fullName = `${user.first_name} ${user.last_name ?? ''}`.trim();

  return (
    <Dropdown
      width={240}
      align={variant === 'card' ? 'left' : 'right'}
      openUpward={variant === 'card'}
      trigger={({ onClick, isOpen }) =>
        variant === 'card' ? (
          <button
            type="button"
            onClick={onClick}
            className={`flex w-full items-center gap-2.5 rounded-2xl border border-gray-200 p-2.5 text-left transition-colors hover:bg-gray-50 dark:border-gray-800 dark:hover:bg-gray-800 ${
              isOpen ? 'bg-gray-50 dark:bg-gray-800' : ''
            }`}
            aria-label="Open profile menu"
          >
            <Avatar src={user.avatar_url} name={fullName} size={36} />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">{fullName}</p>
              <p className="truncate text-xs text-gray-400">{user.job_title || user.email}</p>
            </div>
            <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 text-gray-400" />
          </button>
        ) : (
          <button
            type="button"
            onClick={onClick}
            className={`flex items-center gap-2 rounded-full p-0.5 transition-colors ${
              isOpen ? 'ring-2 ring-brand-500/40' : ''
            }`}
            aria-label="Open profile menu"
          >
            <Avatar src={user.avatar_url} name={fullName} size={32} />
          </button>
        )
      }
    >
      {(close) => (
        <div className="flex flex-col gap-0.5">
          <div className="flex items-center gap-3 px-3 py-2">
            <Avatar src={user.avatar_url} name={fullName} size={36} />
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">{fullName}</p>
              <p className="truncate text-xs text-gray-400">{user.email}</p>
            </div>
          </div>
          <div className="my-1 border-t border-gray-100 dark:border-gray-800" />

          <DropdownItem
            icon={UserIcon}
            label="View profile"
            onClick={() => {
              navigate('/profile');
              close();
            }}
          />
          <DropdownItem
            icon={KeyRound}
            label="Change password"
            onClick={() => {
              navigate('/profile/password');
              close();
            }}
          />
          <DropdownItem
            icon={Laptop}
            label="Active devices"
            onClick={() => {
              navigate('/devices');
              close();
            }}
          />
          <DropdownItem
            icon={resolvedTheme === 'dark' ? Sun : Moon}
            label={resolvedTheme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            onClick={toggle}
          />

          <div className="my-1 border-t border-gray-100 dark:border-gray-800" />
          <DropdownItem
            icon={LogOut}
            label="Log out"
            danger
            onClick={() => {
              void logout();
              close();
            }}
          />
        </div>
      )}
    </Dropdown>
  );
}
